import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { Duration } from "luxon";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { HeadsUpLabsBadge } from "@/components/heads-up/cards/common/heads-up-labs-badge";
import { formatLiveItemTimestamp } from "@/components/heads-up/cards/common/date-utils";
import { HeadsUpLiveExplainer } from "@/components/heads-up/cards/common/heads-up-live-explainer";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import { useHeadsUpPreviewHover } from "@/components/heads-up/cards/common/use-heads-up-preview-hover";
import { SeeMoreButton, SeeMoreRow } from "@/components/heads-up/cards/common/see-more-button";
import { LivePulseIndicator } from "@/components/heads-up/cards/common/live-pulse-indicator";
import { FloatingNotePreview, HEADS_UP_PREVIEW_PLACEMENT } from "@/components/item-preview";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { FC } from "react";
import type { LiveSmartCard } from "@/components/heads-up/heads-up.types";
import type {
  HeadsUpCardItemMeta,
  HeadsUpCardRendererAnalytics,
} from "@/components/heads-up/cards/registry";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type { Maybe } from "@/domains/common/types";
import { LiveEntryFeedback } from "@/components/heads-up/cards/live-entry-feedback";
import { HeadsUpItemSource, useHeadsUpBaseContextPayload } from "@/components/heads-up/analytics";
import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSChip,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSChipVariant,
  MDSTextOverflow,
} from "@/mds";
import { IntervalObservable } from "@/store/common";
import { useHeadsUpContext } from "@/components/heads-up/context";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

const ONE_MINUTE = Duration.fromObject({ minutes: 1 });

type Props = {
  card: LiveSmartCard;
  isLiveActive?: boolean;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

interface SeenEntryState {
  scopeKey: string;
  ids: Set<string>;
  initialized: boolean;
}

function hasEntryId<T extends { entryId: string | null }>(
  item: T
): item is T & { entryId: string } {
  return item.entryId !== null;
}

export const LiveSmartCardRenderer: FC<Props> = observer(
  ({ card, isLiveActive = false, getNotePreviewInfo, analytics }) => {
    const clickHandlers = useHeadsUpClick();
    const headsUpContext = useHeadsUpContext();
    const basePayload = useHeadsUpBaseContextPayload();
    const timeInterval = useMemo(() => new IntervalObservable({ duration: ONE_MINUTE }), []);
    const seenEntryIdsRef = useRef<SeenEntryState>({
      scopeKey: "",
      ids: new Set(),
      initialized: false,
    });
    useEffect(() => () => timeInterval.dispose(), [timeInterval]);

    const sectionTitle = card.value.title ?? "Live";
    const baseItemSource = card.originated_from_find_more
      ? HeadsUpItemSource.FindMore
      : HeadsUpItemSource.Standard;

    const items = useMemo(
      () =>
        card.value.items
          .map((item, index) => ({
            rowKey: `${item.note_id}:${index}`,
            noteId: item.note_id,
            noteTitle: item.note_title,
            topic: item.primary_label,
            insight: item.secondary_label,
            quote: item.quote ?? null,
            addedAt: item.added_at ?? null,
            recordingOffsetSeconds: item.recording_offset_seconds ?? null,
            entryId: ((item as Record<string, unknown>).entry_id as string | null) ?? null,
          }))
          .sort((a, b) => {
            if (!a.addedAt && !b.addedAt) return 0;
            if (!a.addedAt) return 1;
            if (!b.addedAt) return -1;
            return b.addedAt.localeCompare(a.addedAt);
          })
          .map((item, index) => ({
            ...item,
            positionIndex: index,
          })),
      [card.value.items]
    );

    const initialCount = 3;
    const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
      cardId: card.id,
      items,
      initialCount,
      step: 5,
    });
    const isExpanded = visibleItems.length > initialCount;
    const entryScopeKey = `${card.id}:${headsUpContext.context.contextId ?? "none"}`;

    useEffect(() => {
      if (!isLiveActive) return;

      if (seenEntryIdsRef.current.scopeKey !== entryScopeKey) {
        seenEntryIdsRef.current = { scopeKey: entryScopeKey, ids: new Set(), initialized: false };
      }

      if (!seenEntryIdsRef.current.initialized) {
        seenEntryIdsRef.current = {
          scopeKey: entryScopeKey,
          ids: new Set(items.filter(hasEntryId).map(item => item.entryId)),
          initialized: true,
        };
        return;
      }

      for (const item of visibleItems) {
        if (!item.entryId || seenEntryIdsRef.current.ids.has(item.entryId)) continue;

        seenEntryIdsRef.current.ids.add(item.entryId);
        trackEvent(TrackedEvent.HeadsUpLiveEntryAppear, {
          ...basePayload,
          smart_card_id: card.id,
          entry_id: item.entryId,
          note_id: item.noteId,
          note_title: item.noteTitle,
          topic: item.topic,
          insight: item.insight,
          item_position_index: item.positionIndex,
          quote: item.quote,
          has_feedback_controls: true,
          recording_offset_seconds: item.recordingOffsetSeconds,
        });
      }
    }, [basePayload, card.id, entryScopeKey, isLiveActive, items, visibleItems]);

    const buildAnalyticsMeta = useCallback(
      (item: (typeof items)[number]): HeadsUpCardItemMeta => ({
        itemKind: "live_note",
        itemId: item.noteId,
        itemTitle: item.topic,
        itemPositionIndex: item.positionIndex,
        itemSource: baseItemSource,
      }),
      [baseItemSource]
    );

    const noteClickHandlers = useMemo(() => {
      const handlers = new Map<string, () => void>();
      for (const item of items) {
        handlers.set(item.rowKey, () => {
          analytics?.onItemClick?.(buildAnalyticsMeta(item));
          const handler = clickHandlers?.noteClick(item.noteId);
          handler?.({ preventDefault: () => {} } as React.MouseEvent<HTMLElement>);
        });
      }
      return handlers;
    }, [analytics, buildAnalyticsMeta, clickHandlers, items]);

    const headerExtra = (
      <HeaderExtra>
        <HeadsUpLabsBadge />
        {isLiveActive ? <LivePulseIndicator /> : null}
      </HeaderExtra>
    );

    return (
      <SmartCardSection>
        <CollapsibleSectionHeader
          title={sectionTitle}
          titleAccessory={
            <HeadsUpLiveExplainer
              analytics={{
                source: "content_card",
                smartCardId: card.id,
                isLiveActive,
              }}
            />
          }
          headerExtra={headerExtra}
          onCollapseChange={c => c && reset()}
        >
          <LiveList>
            {visibleItems.map(item => (
              <LiveNoteItemRow
                key={item.rowKey}
                item={item}
                smartCardId={card.id}
                notePreviewInfo={getNotePreviewInfo?.(item.noteId)}
                onPress={noteClickHandlers.get(item.rowKey)}
                timeInterval={timeInterval}
                onPreviewOpen={() => analytics?.onItemPreviewOpen?.(buildAnalyticsMeta(item))}
              />
            ))}
            <SeeMoreButton hasMore={hasMore} onClick={showAll} />
            {!hasMore && isExpanded && (
              <SeeMoreRow onClick={reset}>
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.Medium}
                  color={MDSTextColor.Secondary}
                >
                  Show less
                </MDSText>
              </SeeMoreRow>
            )}
          </LiveList>
        </CollapsibleSectionHeader>
      </SmartCardSection>
    );
  }
);

interface LiveNoteItemRowProps {
  item: {
    rowKey: string;
    noteId: string;
    noteTitle: string;
    topic: string;
    insight: string | null;
    quote: string | null;
    addedAt: string | null;
    recordingOffsetSeconds: number | null;
    entryId: string | null;
  };
  smartCardId: string;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
  onPress?: () => void;
  timeInterval: IntervalObservable;
  onPreviewOpen?: () => void;
}

const LiveNoteItemRow: FC<LiveNoteItemRowProps> = observer(
  ({ item, smartCardId, notePreviewInfo, onPress, timeInterval, onPreviewOpen }) => {
    const { hoverState, handleMouseEnter, handleMouseLeave } = useHeadsUpPreviewHover({
      noteId: item.noteId,
      notePreviewInfo,
      onPreviewOpen,
    });

    return (
      <LiveItemWrapper>
        <LiveItem>
          <TitleRow>
            <MDSIcon
              kind={MDSIconKind.CircleDot}
              color={MDSIconColor.Emphasis}
              size={12}
              boxSize={12}
            />
            <TopicMetaRow>
              <TopicText
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.SemiBold}
                color={MDSTextColor.Emphasis}
                overflow={MDSTextOverflow.Ellipsis}
              >
                {item.topic}
              </TopicText>
              <MDSIcon kind={MDSIconKind.Dot} color={MDSIconColor.Emphasis} size={6} />
              <MDSText
                variant={MDSTextVariant.TextXs}
                color={MDSTextColor.Emphasis}
                weight={MDSTextWeight.Regular}
                overflow={MDSTextOverflow.Ellipsis}
              >
                {formatLiveItemTimestamp({
                  addedAt: item.addedAt,
                  recordingOffsetSeconds: item.recordingOffsetSeconds,
                  tick: timeInterval.value,
                })}
              </MDSText>
            </TopicMetaRow>
            {item.entryId ? (
              <LiveEntryFeedback
                smartCardId={smartCardId}
                entryId={item.entryId}
                noteId={item.noteId}
                noteTitle={item.noteTitle}
                topic={item.topic}
                insight={item.insight}
                hasQuote={Boolean(item.quote)}
                recordingOffsetSeconds={item.recordingOffsetSeconds}
              />
            ) : null}
          </TitleRow>
          {item.quote ? (
            <QuoteBlock>
              <MDSText
                variant={MDSTextVariant.ParagraphS}
                weight={MDSTextWeight.Regular}
                color={MDSTextColor.Secondary}
              >
                {item.quote}
              </MDSText>
            </QuoteBlock>
          ) : null}
          <DescriptionText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.Regular}>
            {item.insight}
          </DescriptionText>
          {item.noteTitle ? (
            <SmallNoteChip
              icon={MDSIconKind.Document}
              variant={MDSChipVariant.Small}
              aria-label={item.noteTitle}
              onPress={onPress}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {item.noteTitle}
            </SmallNoteChip>
          ) : null}
        </LiveItem>

        {hoverState.hovering && notePreviewInfo?.noteObservable && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={notePreviewInfo.noteObservable}
            placement={HEADS_UP_PREVIEW_PLACEMENT}
          />
        )}
      </LiveItemWrapper>
    );
  }
);

const LiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  margin-top: 2px;
`;

const LiveItemWrapper = styled.div`
  position: relative;
`;

const HeaderExtra = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  margin-left: auto;
`;

const LiveItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: 6px ${({ theme }) => theme.spacing["2"]};
  border-radius: 6px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const TopicMetaRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const TopicText = styled(MDSText)`
  min-width: 0;
`;

const DescriptionText = styled(MDSText)`
  padding-left: 18px;
`;

const QuoteBlock = styled.div`
  margin-left: 18px;
  padding-left: ${({ theme }) => theme.spacing["2"]};
  border-left: 2px solid ${({ theme }) => theme.colors.border.default};
  font-style: italic;
`;

const SmallNoteChip = styled(MDSChip)`
  align-self: flex-start;
  margin-left: 18px;
`;
